import WorkbenchWCanvasSlotActionsGenerator, {
  SET_TARGET_DEFINITION_DIRTY,
  SET_IS_DROPPED,
  SET_DATA_FROM_TARGET,
  FETCH_DATA_FROM_TARGET_FOR_GRAPHS,
  UPDATE_STREAMING_OPTION,
  SET_TARGET_DEFINITION,
  SET_TARGET_ENVIRONMENT,
} from '@/views/workbenchCanvas/store/WorkbenchWCanvasSlotActionsGenerator'
import definitionSerializer from '@/appComponents/oldWorkbench/targetDefinition/services/serializer'
import transformAttributes from '@/components/common/attributes'
import { convertViewingStreamForAM } from '@/appLogic/targets/conversions'
import { filter, map } from 'lodash'
import {
  fetchSingleTarget,
  fetchNetworksConfig,
  fetchTargetData,
  fetchTargetDefinition,
  fetchAgeDistribution,
  fetchIncomeDistribution,
  fetchEsDateRanges,
} from '@/api'
import {
  SET_AUDIENCE_SIZE,
  SET_DATA_SOURCES,
  SET_DAY_PARTS,
  SET_HISTORICAL_VIEWERSHIP,
  SET_UNIVERSE,
  SET_AGE_DISTRIBUTION,
  SET_INCOME_DISTRIBUTION,
  SHOW_DROPPED_ATTRIBUTES_MODEL,
} from './mutations'
import {
  REMOVE_VIEW_ONLY,
  SET_DEFINITION,
  SET_DROPPED_ATTRIBUTES_IN_TD,
  SET_TARGET_HAD_CHANGED,
  SET_TO_VIEW_ONLY,
  INITIALIZE_TARGET_DEF,
} from '@/appComponents/oldWorkbench/targetDefinition/store/actions'
import {
  SET_FETCHING_TARGET,
  SET_FETCHING_WORKBENCH_DATA,
  SET_LOADING,
  SET_METRICS,
  SET_QUICK_ADD,
} from '@/views/workbenchCanvas/store/mutations'
import {
  INIT_DATE_RANGE,
  UPDATE_END_DATE,
  UPDATE_START_DATE,
} from '@/appComponents/oldWorkbench/dateRange/store/actions'
import ConfigAPI from '@/api/ConfigAPI'
import {
  SET_CURRENT_TARGET,
  SET_SELECTED_VIEWERSHIP_NETWORKS,
  SET_TARGET_DROPPED_ATTRIBUTES as SET_TARGET_DROPPED_ATTRIBUTES_CURRENT_TARGET,
} from '@/appLogic/targets/currentTarget/store/actions'
import { SET_NETWORKS, SET_NETWORKS_GROUPINGS } from '@/appLogic/networks/store/constants'
import { hasRemovedAttributes } from '@/appLogic/targets/TargetLogic'
import {
  GET_REVISION_HISTORY,
  FORWARD_REVISION_HISTORY,
  SET_TARGET_BRANCH,
  SET_PRIMARY_BRANCH,
  SET_MISSING_BRANCH,
} from '@/appLogic/revisionHistory/store/actions'
import generateLabel from '@/appLogic/targetDefinition/LabelMoveGenerator'
import Bluebird from 'bluebird'
import { waitForRaceCondition } from '@/common/U_T'

/** ***************ACTIONS VARIABLE NAMES*******************/
export const SET_DATA_FOR_AGE_AND_INCOME_GRAPHS = 'SET_DATA_FOR_AGE_AND_INCOME_GRAPHS'
export const SET_TARGET_DROPPED_ATTRIBUTES = 'SET_TARGET_DROPPED_ATTRIBUTES'
export const SET_TRIGGERED_ONCE = 'SET_TRIGGERED_ONCE'

const lasConfigApi = ConfigAPI.getConfigFilesForConfigType('las-config.json')

class WorkbenchActions extends WorkbenchWCanvasSlotActionsGenerator {
  initActionBody({ dispatch, commit, getters, state }, id) {
    // TODO we need to set the values for the Revision History

    commit(SET_LOADING, true)
    commit(SET_FETCHING_WORKBENCH_DATA, true)
    // let manuallySetDateRange = false
    let isDirty = false
    Bluebird.props({
      revisionHistory: dispatch(GET_REVISION_HISTORY, { targetId: id }),
      config: lasConfigApi.fetchConfig(),
      // TODO need to adjust for full target
      target: fetchSingleTarget({ target_id: id }).then(resp => resp.data),
    })
      .then(async data => {
        const { config, target } = data

        const esDateRanges = await fetchEsDateRanges(target.target_datasource)

        commit(SET_CURRENT_TARGET, target)
        dispatch(SET_TARGET_BRANCH, { name: target.display_name })
        if (!getters.isTargetActivated && !target.viewing_stream) {
          target.viewing_stream = 'lsd'
          commit(SET_TARGET_HAD_CHANGED)
          isDirty = true
        }
        if (!getters.revisionHistoryCurrentBranch) {
          dispatch(SET_MISSING_BRANCH, { id: target.id, display_name: target.display_name })
        } else {
          dispatch(SET_PRIMARY_BRANCH, { name: target.display_name })
        }
        if (!getters.hasCreateEditPermissions) {
          dispatch(SET_TO_VIEW_ONLY)
        } else {
          dispatch(REMOVE_VIEW_ONLY)
        }
        return lasConfigApi
          .fetchDatasets(config.datasets, target.target_datasource === 'ncs' ? 'nielsen' : target.target_datasource)
          .then(datasets => {
            return fetchNetworksConfig().then(networks => ({ networks, datasets, config, esDateRanges }))
          })
      })
      .then(({ datasets, networks, config, esDateRanges }) => {
        // datasets and datasources are used interchangeably
        commit(
          SET_DATA_SOURCES,
          datasets.map(dataset => Object.freeze(transformAttributes(dataset, dataset.id))),
        )
        commit(SET_QUICK_ADD, config.quick_add)
        commit(SET_NETWORKS, networks.networks)
        commit(SET_NETWORKS_GROUPINGS, networks.categories)
        // TODO make sure that we are getting the categories here...
        commit(SET_DAY_PARTS, config.day_parts)
        dispatch(INIT_DATE_RANGE, esDateRanges)
        // set data range...use targets date range if it is valid otherwise keep default
        const ct = getters.currentTarget
        if (getters.isValidStartDate(ct.target_definition.startDate)) {
          dispatch(UPDATE_START_DATE, ct.target_definition.startDate)
          dispatch(UPDATE_END_DATE, ct.target_definition.endDate)
        } else {
          isDirty = true // manuallySetDateRange = true
        }
        // if (ct.target_definition.hasOwnProperty('viewSource')) {
        //   dispatch(UPDATE_STREAMING_OPTION, ct.target_definition.viewSource)
        // }
        commit(SET_DEFINITION, definitionSerializer.deserialize(ct.target_definition.query, getters))
        dispatch(UPDATE_STREAMING_OPTION, ct.viewing_stream)
        // By default if a user has not clarified what networks
        // he/she would like to see we default to the turner networks
        ct.workbench_data.networks
          ? commit(SET_SELECTED_VIEWERSHIP_NETWORKS, ct.workbench_data.networks)
          : commit(
              SET_SELECTED_VIEWERSHIP_NETWORKS,
              map(
                filter(networks.networks, network => network.turner),
                network => network.id,
              ),
            )
        if (getters.isTargetActivated) {
          dispatch(SET_DATA_FROM_TARGET, {
            metrics: ct.workbench_data.metrics,
            audienceSize: ct.workbench_data.audience_size,
            historicalViewership: ct.workbench_data.viewership_historical,
            universe: ct.workbench_data.universe,
          })
          commit(SET_LOADING, false)
        } else {
          return dispatch(FETCH_DATA_FROM_TARGET_FOR_GRAPHS).then(() => {
            dispatch(INITIALIZE_TARGET_DEF, getters.getDefinition)
          })
        }
      })
      .then(() => {
        dispatch(SET_TARGET_DEFINITION_DIRTY, isDirty)
      })
      .finally(() => {
        if (getters.isFetchingWorkbenchData) {
          commit(SET_FETCHING_WORKBENCH_DATA, false)
          dispatch(FETCH_DATA_FROM_TARGET_FOR_GRAPHS)
        }
      })
  }
  targetDataToSave({ state, getters }) {
    const rhpm = getters.revisionHistoryCurrentMove
    if (rhpm) {
      // TODO Revision History should include the Networks
      const td = rhpm.target_data
      return {
        id: getters.currentTarget.id,
        username: getters.currentTarget.username,
        display_name: getters.currentTarget.display_name,
        target: getters.currentTarget.target,
        target_datasource: getters.currentTarget.target_datasource,
        target_definition: getters.targetDefinition,
        audience_size: td.audienceSize,
        universe: getters.universe,
        metrics: td.metrics,
        viewership_historical: td.historicalViewership,
        networks: getters.selectedViewershipNetworks, // NETWORK
        age_data: td.age,
        income_data: td.income,
        viewing_stream: convertViewingStreamForAM(state.streamingOption),
      }
    } else {
      return {
        id: getters.currentTarget.id,
        username: getters.currentTarget.username,
        display_name: getters.currentTarget.display_name,
        target: getters.currentTarget.target,
        target_datasource: getters.currentTarget.target_datasource,
        target_definition: getters.targetDefinition,
        audience_size: getters.audienceSize,
        universe: getters.universe,
        metrics: state.metrics,
        viewership_historical: getters.historicalViewership,
        networks: getters.selectedViewershipNetworks, // NETWORK
        age_data: getters.ageData,
        income_data: getters.incomeData,
        viewing_stream: convertViewingStreamForAM(state.streamingOption),
      }
    }
  }

  fetchDataFromTargetForGraphs({ dispatch, commit, getters, state }) {
    let theTime = new Date()
    let currentNumber = state.requestNumber
    commit(SET_LOADING, true)
    if (getters.isFetchingWorkbenchData) {
      // return if workbench data is still being fetched
      return
    }
    commit(SET_FETCHING_TARGET, true)

    const targetDefinitionToFetch = getters.targetDefinition
    const ageAndIncome = dispatch(SET_DATA_FOR_AGE_AND_INCOME_GRAPHS).then(ageAndIncome => {
      return Bluebird.props({
        age: ageAndIncome.age,
        income: ageAndIncome.income,
      })
    })

    const freshTargetDefinition = fetchTargetDefinition(targetDefinitionToFetch).then(resp => {
      // set new target definition here
      const freshDefinition = resp.data
      if (currentNumber === state.requestNumber) {
        commit(SET_DROPPED_ATTRIBUTES_IN_TD, definitionSerializer.deserialize(freshDefinition))
      }
      return getters.targetDefinition
    })
    // TODO why do we need to make seperate calls here?
    // combine these calls here.
    const targetData = fetchTargetData(targetDefinitionToFetch)
      .then(resp => {
        const targetData = {
          metrics: resp.metrics,
          audienceSize: resp.audienceSize && resp.audienceSize.audience,
          historicalViewership: resp.historicalViewership,
          universe: resp.universe,
        }
        return targetData
      })
      .catch(e => console.error(e))
    // console.log({
    //   theTime,
    //   isFetchingTarget: getters.isFetchingTarget,
    //   query: targetDefinitionToFetch.query
    // })
    return Bluebird.props({
      ageAndIncome,
      freshTargetDefinition,
      targetData,
    }).then(targetInformation => {
      if (currentNumber === state.requestNumber) {
        if (targetInformation.freshTargetDefinition !== getters.targetDefinition) {
          commit(SET_FETCHING_TARGET, false)
          dispatch(FETCH_DATA_FROM_TARGET_FOR_GRAPHS)
          return null
        } else {
          dispatch(SET_DATA_FROM_TARGET, targetInformation.targetData)
          const lastMove = getters.targetDefinitionLastAttributeCommand
          const userHasMadeAForwardAction = lastMove
          if (userHasMadeAForwardAction) {
            // TODO add the label based off the lastMove
            dispatch(FORWARD_REVISION_HISTORY, {
              targetData: {
                ...targetInformation.targetData,
                ...targetInformation.ageAndIncome,
              },
              targetDefinition: targetInformation.freshTargetDefinition,
              audience: targetInformation.targetData.audienceSize,
              label: generateLabel(lastMove),
            })
          }
          commit(SET_LOADING, false)
          commit(SET_FETCHING_TARGET, false)
        }
      }
    })
  }

  dataFromTargetToSet(commit, data) {
    const { metrics, audienceSize, historicalViewership, universe } = data
    commit(SET_METRICS, metrics)
    commit(SET_AUDIENCE_SIZE, audienceSize)
    commit(SET_HISTORICAL_VIEWERSHIP, historicalViewership)
    commit(SET_UNIVERSE, universe)
  }

  additionalActions() {
    return {
      [SET_DATA_FOR_AGE_AND_INCOME_GRAPHS]: ({ getters, commit }) => {
        const targetDefinitionToFetch = getters.targetDefinition
        return {
          age: fetchAgeDistribution(targetDefinitionToFetch).then(resp => {
            commit(SET_AGE_DISTRIBUTION, resp.data.data)
            return resp.data.data
          }),
          income: fetchIncomeDistribution(targetDefinitionToFetch).then(resp => {
            commit(SET_INCOME_DISTRIBUTION, resp.data.data)
            return resp.data.data
          }),
        }
      },
      [SET_TARGET_ENVIRONMENT]: (context, targetData) => {
        context.dispatch(SET_FETCHING_TARGET, true)
        context.dispatch(SET_TARGET_DEFINITION, targetData.targetDefinition)
        context.dispatch(SET_DATA_FROM_TARGET, targetData.targetData)
        context.commit(SET_AGE_DISTRIBUTION, targetData.targetData.age)
        context.commit(SET_INCOME_DISTRIBUTION, targetData.targetData.income)
        // we have to set a timeout because of the watcher
        setTimeout(() => {
          context.dispatch(SET_FETCHING_TARGET, false)
        }, 50)
      },
      [SET_TARGET_DEFINITION]: (context, targetDefinition) => {
        context.dispatch(UPDATE_START_DATE, targetDefinition.startDate)
        context.dispatch(UPDATE_END_DATE, targetDefinition.endDate)
        context.dispatch(SET_DEFINITION, definitionSerializer.deserialize(targetDefinition.query, context.getters))
        context.commit(SET_SELECTED_VIEWERSHIP_NETWORKS, targetDefinition.networks)
        context.dispatch(UPDATE_STREAMING_OPTION, targetDefinition.viewSource)
      },
      [SET_TARGET_DROPPED_ATTRIBUTES]: ({ getters, commit }) => {
        const waitFunction = () => {
          const AD = getters.dateRange.availableDates
          return AD.length > 0
        }
        const mainFunction = () => {
          const AD = getters.dateRange.availableDates
          const endDate = AD[AD.length - 1].to
          return fetchTargetDefinition({
            ...getters.currentTarget.target_definition,
            endDate,
          }).then(resp => {
            // set new target definition here
            const freshDefinition = resp.data
            commit(SET_DEFINITION, definitionSerializer.deserialize(freshDefinition))
            const isDropped = hasRemovedAttributes(freshDefinition)
            commit(SET_IS_DROPPED, isDropped)
            return getters.targetDefinition
          })
        }
        waitForRaceCondition(waitFunction, mainFunction)
      },
      [SHOW_DROPPED_ATTRIBUTES_MODEL]: context => {
        context.dispatch(SET_TARGET_DROPPED_ATTRIBUTES_CURRENT_TARGET, context.getters.currentTarget.id)
        context.commit(SHOW_DROPPED_ATTRIBUTES_MODEL)
      },
      [SET_TRIGGERED_ONCE]: context => {
        context.commit(SET_TRIGGERED_ONCE)
      },
    }
  }
}

export default WorkbenchActions



// WEBPACK FOOTER //
// ./src/app/views/workbench/LAS/store/WorkbenchActions.js